import React, { useState } from 'react';
import { Lock, ChevronDown, ChevronUp, Database, Eye, Share2, Shield, UserX, Bell, Mail, Globe } from 'lucide-react';

const Section = ({ id, icon: Icon, title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="border border-gray-200 rounded-xl mb-4 overflow-hidden" id={id}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-primary flex-shrink-0" />}
          <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
        <div className="p-5 pt-0 bg-white border-t border-gray-100 text-gray-600 leading-relaxed space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

const DataTable = ({ rows }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="bg-gray-50">
          <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Catégorie</th>
          <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Données collectées</th>
          <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Finalité</th>
          <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Base légale</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
            <td className="p-3 border border-gray-200 font-medium text-gray-700">{row.category}</td>
            <td className="p-3 border border-gray-200 text-gray-600">{row.data}</td>
            <td className="p-3 border border-gray-200 text-gray-600">{row.purpose}</td>
            <td className="p-3 border border-gray-200 text-gray-600">{row.legal}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const PrivacyPolicy = () => {
  const lastUpdate = '30 avril 2026';

  const toc = [
    { id: 'responsable', label: 'Responsable du traitement' },
    { id: 'donnees', label: 'Données collectées' },
    { id: 'finalites', label: 'Finalités et bases légales' },
    { id: 'conservation', label: 'Durée de conservation' },
    { id: 'partage', label: 'Partage des données' },
    { id: 'transferts', label: 'Transferts internationaux' },
    { id: 'securite', label: 'Sécurité des données' },
    { id: 'cookies', label: 'Cookies et traceurs' },
    { id: 'droits', label: 'Vos droits' },
    { id: 'mineurs', label: 'Mineurs' },
    { id: 'ia', label: 'Intelligence artificielle' },
    { id: 'modifications', label: 'Modifications de la politique' },
    { id: 'contact', label: 'Contact & DPO' },
  ];

  const dataRows = [
    {
      category: 'Identité',
      data: 'Nom, prénom, e-mail, téléphone, avatar, biographie, localisation',
      purpose: 'Création et gestion du compte, personnalisation',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Authentification',
      data: 'Mot de passe (hashé), jetons JWT, historique de connexion',
      purpose: 'Sécurisation de l\'accès',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Profil professionnel',
      data: 'Rôle (apprenant/instructeur/entrepreneur/investisseur), secteur, nom d\'entreprise',
      purpose: 'Personnalisation des services, mise en relation',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Progression pédagogique',
      data: 'Cours suivis, leçons complétées, temps d\'étude, scores d\'évaluation, certificats',
      purpose: 'Suivi pédagogique, délivrance de certificats, analytics',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Gamification',
      data: 'Points XP, niveau, badges, séries (streaks), classement',
      purpose: 'Engagement, motivation, leaderboard',
      legal: 'Intérêt légitime'
    },
    {
      category: 'Coaching',
      data: 'Plans de coaching, sessions, indicateurs de performance, objectifs',
      purpose: 'Accompagnement personnalisé',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Contenu généré',
      data: 'Avis, notes, messages de chat, questions/réponses',
      purpose: 'Animation de la communauté, amélioration du contenu',
      legal: 'Consentement / intérêt légitime'
    },
    {
      category: 'Médias',
      data: 'Photos, vidéos, fichiers PDF et audio uploadés',
      purpose: 'Diffusion du contenu pédagogique',
      legal: 'Exécution du contrat'
    },
    {
      category: 'Paiements',
      data: 'Historique d\'abonnements, plan souscrit (données bancaires traitées par notre prestataire)',
      purpose: 'Facturation, gestion des accès',
      legal: 'Obligation légale / exécution du contrat'
    },
    {
      category: 'Technique',
      data: 'Adresse IP, type de navigateur, système d\'exploitation, logs de connexion',
      purpose: 'Sécurité, débogage, statistiques d\'usage',
      legal: 'Intérêt légitime'
    },
    {
      category: 'Préférences',
      data: 'Langue, thème (clair/sombre), paramètres de notification et de confidentialité',
      purpose: 'Personnalisation de l\'expérience',
      legal: 'Consentement'
    },
    {
      category: 'Bourse d\'idées',
      data: 'Description de projet, secteur d\'activité, documents soumis',
      purpose: 'Mise en relation entrepreneurs/investisseurs',
      legal: 'Consentement'
    },
  ];

  return (
    <div className="min-h-full bg-gray-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-4 mb-4">
            <Lock className="w-10 h-10 opacity-80" />
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Politique de confidentialité</h1>
              <p className="text-white/80 mt-1">Dernière mise à jour : {lastUpdate}</p>
            </div>
          </div>
          <p className="text-lg text-white/90 max-w-2xl">
            Innovactive360° s'engage à protéger vos données personnelles. Ce document explique
            quelles données nous collectons, pourquoi et comment vous pouvez les contrôler.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar TOC */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white border border-gray-200 rounded-xl p-4 sticky top-24">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Table des matières</h3>
              <nav className="space-y-1">
                {toc.map(item => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block text-sm text-gray-600 hover:text-emerald-600 py-1 px-2 rounded hover:bg-emerald-50 transition-colors"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <Section id="responsable" icon={Shield} title="1. Responsable du traitement">
              <p>
                Le responsable du traitement de vos données personnelles est :
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-2 space-y-1">
                <p><strong>Innovactive360°</strong></p>
                <p>Parakou, Bénin</p>
                <p>E-mail : <a href="mailto:contact@innovactive360.com" className="text-emerald-600 hover:underline">contact@innovactive360.com</a></p>
                <p>Téléphone : (+229) 01 97 50 68 15</p>
              </div>
              <p>
                Innovactive360° traite vos données dans le respect des lois et réglementations
                applicables en matière de protection des données personnelles, notamment la
                législation béninoise sur la protection des données, et le cas échéant le
                Règlement Général sur la Protection des Données (RGPD) de l'Union Européenne
                pour les utilisateurs résidant dans l'UE.
              </p>
            </Section>

            <Section id="donnees" icon={Database} title="2. Données collectées">
              <p>
                Nous collectons vos données personnelles de trois manières : les informations que
                <strong> vous nous fournissez directement</strong>, celles générées par{' '}
                <strong>votre utilisation de la Plateforme</strong>, et celles issues de{' '}
                <strong>services tiers</strong> (connexion via Google, par exemple).
              </p>
              <DataTable rows={dataRows} />
              <p className="mt-4 text-sm text-gray-500">
                Nous ne collectons jamais de données sensibles (origine ethnique, opinions politiques,
                données de santé, etc.) sauf consentement explicite et nécessité avérée.
              </p>
            </Section>

            <Section id="finalites" icon={Eye} title="3. Finalités et bases légales">
              <p>Nous utilisons vos données pour les finalités suivantes :</p>
              <ul className="list-disc pl-5 space-y-3 mt-2">
                <li>
                  <strong>Fourniture du service :</strong> création et gestion de votre compte,
                  accès aux cours, délivrance de certificats, coaching, messagerie. Base légale :
                  exécution du contrat.
                </li>
                <li>
                  <strong>Personnalisation :</strong> recommandations de cours, adaptation de
                  l'interface, contenu adapté à votre profil. Base légale : intérêt légitime /
                  consentement.
                </li>
                <li>
                  <strong>Gamification et engagement :</strong> calcul et affichage des XP,
                  badges, classements, séries. Base légale : intérêt légitime.
                </li>
                <li>
                  <strong>Communication :</strong> notifications de cours, rappels, newsletters,
                  mises à jour importantes. Base légale : consentement (marketing) / exécution
                  du contrat (notifications essentielles).
                </li>
                <li>
                  <strong>Sécurité :</strong> détection des fraudes, protection contre les
                  accès non autorisés, journaux d'audit. Base légale : intérêt légitime / obligation légale.
                </li>
                <li>
                  <strong>Amélioration de la Plateforme :</strong> analyse d'usage anonymisée,
                  débogage, développement de nouvelles fonctionnalités. Base légale : intérêt
                  légitime.
                </li>
                <li>
                  <strong>Obligations légales :</strong> facturation, archivage comptable,
                  réponse aux demandes des autorités compétentes. Base légale : obligation légale.
                </li>
                <li>
                  <strong>Bourse d'idées / Mise en relation :</strong> partage de votre profil
                  entrepreneur avec des investisseurs potentiels selon vos préférences.
                  Base légale : consentement.
                </li>
              </ul>
            </Section>

            <Section id="conservation" icon={Database} title="4. Durée de conservation">
              <div className="overflow-x-auto mt-2">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Type de données</th>
                      <th className="text-left p-3 border border-gray-200 font-semibold text-gray-700">Durée de conservation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Données de compte actif', 'Durée de la relation contractuelle + 3 ans'],
                      ['Données de progression pédagogique', 'Durée de la relation contractuelle + 5 ans'],
                      ['Certificats émis', '10 ans (obligation légale de vérifiabilité)'],
                      ['Données de paiement / factures', '10 ans (obligation comptable)'],
                      ['Logs de connexion et sécurité', '12 mois glissants'],
                      ['Messages de chat', 'Durée du cours + 1 an'],
                      ['Compte supprimé (données d\'usage)', '90 jours post-suppression, puis effacement'],
                      ['Cookies non essentiels', '13 mois maximum'],
                    ].map(([type, duration], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="p-3 border border-gray-200 text-gray-700">{type}</td>
                        <td className="p-3 border border-gray-200 text-gray-600">{duration}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3">
                À l'expiration de ces durées, vos données sont soit supprimées définitivement,
                soit anonymisées de façon irréversible pour des finalités statistiques.
              </p>
            </Section>

            <Section id="partage" icon={Share2} title="5. Partage des données">
              <p>
                Innovactive360° <strong>ne vend jamais vos données personnelles</strong> à des
                tiers. Nous pouvons partager vos données dans les cas suivants :
              </p>
              <ul className="list-disc pl-5 space-y-3 mt-2">
                <li>
                  <strong>Prestataires techniques (sous-traitants) :</strong> ces partenaires
                  traitent vos données pour notre compte, dans le cadre strict de leur mission
                  et sous notre responsabilité :
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li><strong>Supabase</strong> (PostgreSQL) — hébergement de la base de données et authentification</li>
                    <li><strong>Cloudinary</strong> — stockage et diffusion des médias (images, vidéos, documents)</li>
                    <li><strong>Vercel</strong> — hébergement et déploiement du frontend</li>
                    <li><strong>Hostinger</strong> — hébergement du serveur backend (VPS)</li>
                    <li><strong>Prestataire de paiement</strong> — traitement sécurisé des transactions (données bancaires non stockées par Innovactive360°)</li>
                  </ul>
                </li>
                <li>
                  <strong>Instructeurs :</strong> si vous êtes inscrit(e) à un cours, l'instructeur
                  peut accéder à votre progression et à vos questions dans le cadre du cours.
                </li>
                <li>
                  <strong>Investisseurs (Bourse d'idées) :</strong> uniquement si vous avez
                  explicitement soumis un projet et consenti à sa mise en visibilité.
                </li>
                <li>
                  <strong>Autorités compétentes :</strong> en réponse à une obligation légale,
                  une décision de justice ou pour protéger les droits d'Innovactive360°.
                </li>
                <li>
                  <strong>Cession d'activité :</strong> en cas de fusion, acquisition ou cession
                  totale ou partielle d'Innovactive360°, vos données pourront être transférées
                  au repreneur, sous réserve de votre information préalable.
                </li>
              </ul>
            </Section>

            <Section id="transferts" icon={Globe} title="6. Transferts internationaux de données">
              <p>
                Vos données sont principalement hébergées en Europe (Supabase EU, Vercel EU)
                et aux États-Unis (Cloudinary). Pour tout transfert hors de l'Espace Économique
                Européen, nous mettons en place des garanties appropriées, notamment :
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li>Clauses contractuelles types approuvées par la Commission Européenne.</li>
                <li>Certification Privacy Shield ou équivalent pour les prestataires américains.</li>
                <li>Évaluation de l'impact du transfert (TIA) lorsque requise.</li>
              </ul>
              <p>
                Vous pouvez obtenir des informations sur les garanties mises en place en nous
                contactant à <a href="mailto:contact@innovactive360.com" className="text-emerald-600 hover:underline">contact@innovactive360.com</a>.
              </p>
            </Section>

            <Section id="securite" icon={Shield} title="7. Sécurité des données">
              <p>
                Innovactive360° met en œuvre des mesures techniques et organisationnelles
                appropriées pour protéger vos données contre tout accès non autorisé, toute
                divulgation, altération ou destruction :
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li><strong>Chiffrement :</strong> communications chiffrées via HTTPS/TLS, mots de passe hashés (bcrypt).</li>
                <li><strong>Authentification :</strong> jetons JWT avec expiration courte, refresh tokens sécurisés.</li>
                <li><strong>Contrôle d'accès :</strong> accès aux données limité selon les rôles (RBAC).</li>
                <li><strong>Rate limiting :</strong> limitation du nombre de requêtes pour prévenir les attaques par force brute.</li>
                <li><strong>En-têtes de sécurité :</strong> configuration Helmet pour prévenir les attaques XSS, clickjacking, etc.</li>
                <li><strong>CORS :</strong> liste blanche des origines autorisées.</li>
                <li><strong>Surveillance :</strong> journaux d'accès et alertes en cas d'anomalie.</li>
              </ul>
              <p>
                En cas de violation de données susceptible d'engendrer un risque pour vos droits
                et libertés, nous nous engageons à vous notifier dans les délais légaux applicables.
              </p>
            </Section>

            <Section id="cookies" icon={Eye} title="8. Cookies et traceurs">
              <p>
                La Plateforme utilise des cookies et technologies similaires pour assurer son
                fonctionnement et améliorer votre expérience. Nous distinguons :
              </p>
              <ul className="list-disc pl-5 space-y-3 mt-2">
                <li>
                  <strong>Cookies essentiels :</strong> nécessaires au fonctionnement de la
                  Plateforme (session, authentification, préférences de langue/thème).
                  Ils ne peuvent pas être désactivés.
                </li>
                <li>
                  <strong>Cookies de performance :</strong> collectent des statistiques d'usage
                  anonymisées pour améliorer la Plateforme. Soumis à votre consentement.
                </li>
                <li>
                  <strong>Cookies de personnalisation :</strong> mémorisent vos préférences
                  pour personnaliser votre expérience. Soumis à votre consentement.
                </li>
              </ul>
              <p>
                Vous pouvez gérer vos préférences de cookies depuis vos{' '}
                <a href="/settings" className="text-emerald-600 hover:underline">paramètres de compte</a>{' '}
                ou via les paramètres de votre navigateur. Le refus des cookies non essentiels
                n'affecte pas l'accès aux fonctionnalités principales.
              </p>
            </Section>

            <Section id="droits" icon={UserX} title="9. Vos droits sur vos données">
              <p>
                Conformément aux réglementations applicables, vous disposez des droits suivants
                sur vos données personnelles :
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {[
                  { title: 'Droit d\'accès', desc: 'Obtenir une copie de vos données personnelles que nous traitons.' },
                  { title: 'Droit de rectification', desc: 'Corriger des données inexactes ou incomplètes vous concernant.' },
                  { title: 'Droit à l\'effacement', desc: 'Demander la suppression de vos données dans les cas prévus par la loi.' },
                  { title: 'Droit à la portabilité', desc: 'Recevoir vos données dans un format structuré et lisible par machine.' },
                  { title: 'Droit d\'opposition', desc: 'Vous opposer au traitement fondé sur notre intérêt légitime.' },
                  { title: 'Droit de limitation', desc: 'Demander la suspension temporaire du traitement de vos données.' },
                  { title: 'Retrait du consentement', desc: 'Retirer à tout moment un consentement préalablement donné.' },
                  { title: 'Droit de réclamation', desc: 'Saisir l\'autorité de contrôle compétente en matière de protection des données.' },
                ].map((right, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <p className="font-semibold text-gray-700 text-sm">{right.title}</p>
                    <p className="text-gray-500 text-sm mt-1">{right.desc}</p>
                  </div>
                ))}
              </div>
              <p className="mt-4">
                Pour exercer vos droits, contactez-nous à{' '}
                <a href="mailto:contact@innovactive360.com" className="text-emerald-600 hover:underline">
                  contact@innovactive360.com
                </a>{' '}
                avec une copie d'un justificatif d'identité. Nous répondrons dans un délai de
                <strong> 30 jours</strong> (délai pouvant être étendu à 3 mois pour les demandes
                complexes, avec notification préalable).
              </p>
              <p>
                Vous pouvez également accéder directement à la plupart de vos données et les
                modifier depuis votre{' '}
                <a href="/profile" className="text-emerald-600 hover:underline">profil</a> et vos{' '}
                <a href="/settings" className="text-emerald-600 hover:underline">paramètres</a>.
              </p>
            </Section>

            <Section id="mineurs" icon={Shield} title="10. Protection des mineurs">
              <p>
                La Plateforme est destinée à des personnes âgées d'au moins <strong>13 ans</strong>.
                Les utilisateurs de moins de 18 ans doivent obtenir le consentement de leur
                représentant légal avant de s'inscrire.
              </p>
              <p>
                Si nous avons connaissance qu'un enfant de moins de 13 ans a créé un compte
                sans consentement parental, nous supprimerons immédiatement les données
                collectées et désactiverons le compte concerné.
              </p>
              <p>
                Les représentants légaux peuvent contacter{' '}
                <a href="mailto:contact@innovactive360.com" className="text-emerald-600 hover:underline">
                  contact@innovactive360.com
                </a>{' '}
                pour toute demande concernant le compte d'un mineur.
              </p>
            </Section>

            <Section id="ia" icon={Bell} title="11. Intelligence artificielle et données">
              <p>
                Innovactive360° intègre un assistant IA conversationnel. Vos interactions
                avec cet assistant peuvent être utilisées pour :
              </p>
              <ul className="list-disc pl-5 space-y-2 mt-2">
                <li>Répondre à vos questions en temps réel.</li>
                <li>Améliorer la qualité des réponses (sous forme anonymisée uniquement).</li>
              </ul>
              <p>
                Les conversations avec l'assistant IA sont conservées pendant votre session
                et peuvent être accessibles dans votre historique. Elles ne sont pas partagées
                avec des tiers sans votre consentement.
              </p>
              <p>
                Aucune décision entièrement automatisée ayant des effets juridiques significatifs
                n'est prise sur la base de vos données sans intervention humaine.
              </p>
            </Section>

            <Section id="modifications" icon={Bell} title="12. Modifications de la politique">
              <p>
                Innovactive360° peut mettre à jour la présente Politique de confidentialité
                pour refléter l'évolution de ses pratiques ou des exigences légales.
              </p>
              <p>
                En cas de modification substantielle, vous serez informé(e) par e-mail et/ou
                par une notification visible sur la Plateforme au moins <strong>30 jours avant</strong>
                l'entrée en vigueur des changements.
              </p>
              <p>
                Nous vous encourageons à consulter régulièrement cette page pour rester informé(e).
                La date de dernière mise à jour est toujours indiquée en haut de ce document.
              </p>
            </Section>

            <Section id="contact" icon={Mail} title="13. Contact et délégué à la protection des données">
              <p>
                Pour toute question relative à cette Politique ou pour exercer vos droits,
                vous pouvez nous contacter :
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-2 space-y-2">
                <p><strong>Innovactive360° — Protection des données</strong></p>
                <p>Parakou, Bénin</p>
                <p>
                  E-mail :{' '}
                  <a href="mailto:contact@innovactive360.com" className="text-emerald-600 hover:underline">
                    contact@innovactive360.com
                  </a>
                </p>
                <p>
                  Support :{' '}
                  <a href="mailto:support@innovactive360.com" className="text-emerald-600 hover:underline">
                    support@innovactive360.com
                  </a>
                </p>
                <p>Téléphone : (+229) 01 97 50 68 15</p>
              </div>
              <p>
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez adresser
                une réclamation à l'autorité de contrôle compétente dans votre pays de résidence.
              </p>
            </Section>

            <div className="text-center text-sm text-gray-400 mt-8 pb-4">
              Politique de confidentialité — Innovactive360° — Version du {lastUpdate}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
